import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  useToast,
  Alert,
  AlertIcon,
  Badge,
  Divider,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Spinner,
  Image
} from '@chakra-ui/react';
import { FaWallet, FaLink, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { COLORS } from '../utils/constants';
import useAuth from '../store/useAuth';
import useWallet, { WALLET_STATUS } from '../store/useWallet';

const WalletLinkingInterface = ({ onLinkSuccess }) => {
  const [isLinking, setIsLinking] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const { user, walletLogin } = useAuth();
  const { initialize, connectXaman, status, address, errorMessage } = useWallet();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Initialize wallet on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Monitor wallet connection for linking
  useEffect(() => {
    if (status === WALLET_STATUS.CONNECTED && address && isLinking) {
      handleWalletLink(address);
    }
  }, [status, address, isLinking]);

  const handleInitiateLinking = async () => {
    setIsLinking(true);
    setQrCodeUrl('');
    setDeepLink('');
    onOpen();

    try {
      const result = await connectXaman({ autoOpenPopup: false });
      
      if (result.success) {
        setQrCodeUrl(result.qrUrl);
        setDeepLink(result.deepLink);
        
        toast({
          title: 'Wallet Connection Initiated',
          description: 'Please scan the QR code with your XAMAN wallet app',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(result.error?.message || 'Failed to initiate wallet connection');
      }
    } catch (error) {
      console.error('Wallet linking error:', error);
      toast({
        title: 'Connection Error',
        description: error.message || 'Failed to connect wallet',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsLinking(false);
      onClose();
    }
  };

  const handleWalletLink = async (walletAddress) => {
    try {
      // Wallet linking removed in wallet-only migration
      // This component should not be used in wallet-only mode
      throw new Error('Wallet linking not supported in wallet-only mode');
      
    } catch (error) {
      console.error('Wallet linking failed:', error);
      toast({
        title: 'Linking Failed',
        description: error.message || 'Failed to link wallet to your account',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsLinking(false);
    }
  };

  const handleModalClose = () => {
    onClose();
    setIsLinking(false);
    setQrCodeUrl('');
    setDeepLink('');
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box textAlign="center">
          <Icon as={FaWallet} boxSize={12} color={COLORS.primary} mb={4} />
          <Heading size="md" mb={2} color={COLORS.text}>
            Link Your XAMAN Wallet
          </Heading>
          <Text color={COLORS.textSecondary} fontSize="sm">
            Connect your XRPL wallet to enable enhanced features and secure transactions
          </Text>
        </Box>

        {user?.hasConnectedWallet ? (
          <Alert status="success" borderRadius="md" bg="rgba(72, 187, 120, 0.1)" borderColor="green.400">
            <AlertIcon color="green.400" />
            <Box>
              <Text fontWeight="bold" color="green.400">Wallet Already Connected</Text>
              <Text fontSize="sm" color={COLORS.textSecondary} mt={1}>
                Your XAMAN wallet is linked to this account
              </Text>
              {user.walletAddress && (
                <Text fontSize="xs" color={COLORS.textSecondary} mt={2} fontFamily="mono">
                  {user.walletAddress.substring(0, 8)}...{user.walletAddress.substring(user.walletAddress.length - 4)}
                </Text>
              )}
            </Box>
          </Alert>
        ) : (
          <>
            <Alert status="info" borderRadius="md" bg="rgba(66, 153, 225, 0.1)" borderColor="blue.400">
              <AlertIcon color="blue.400" />
              <Box>
                <Text fontWeight="bold" color="blue.400">Why Link Your Wallet?</Text>
                <VStack align="start" spacing={1} mt={2}>
                  <Text fontSize="sm" color={COLORS.textSecondary}>
                    • Enhanced security for your account
                  </Text>
                  <Text fontSize="sm" color={COLORS.textSecondary}>
                    • Access to blockchain-based features
                  </Text>
                  <Text fontSize="sm" color={COLORS.textSecondary}>
                    • Future token rewards and airdrops
                  </Text>
                </VStack>
              </Box>
            </Alert>

            <Button
              leftIcon={<FaLink />}
              onClick={handleInitiateLinking}
              className="primary-button"
              size="lg"
              width="100%"
              isLoading={isLinking}
              loadingText="Connecting..."
            >
              Link XAMAN Wallet
            </Button>

            <Divider />

            <Box p={4} bg="rgba(255, 193, 7, 0.1)" borderRadius="md" borderWidth="1px" borderColor="yellow.400">
              <HStack>
                <Icon as={FaExclamationTriangle} color="yellow.400" />
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="yellow.400">
                    Optional but Recommended
                  </Text>
                  <Text fontSize="xs" color={COLORS.textSecondary} mt={1}>
                    You can continue using Ignita AI without linking a wallet, but connecting one unlocks additional features.
                  </Text>
                </Box>
              </HStack>
            </Box>
          </>
        )}
      </VStack>

      {/* Wallet Connection Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} isCentered>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent
          bg="rgba(0, 0, 0, 0.9)"
          borderColor={status === WALLET_STATUS.ERROR ? "red.500" : COLORS.primary}
          borderWidth="1px"
          color="white"
          maxW="380px"
        >
          <ModalHeader>Link XAMAN Wallet</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="center">
              {status === WALLET_STATUS.ERROR ? (
                <Box 
                  p={4} 
                  bg="rgba(255, 0, 0, 0.1)" 
                  borderRadius="md" 
                  borderWidth="1px" 
                  borderColor="red.500"
                  width="100%"
                >
                  <Text color="red.400" fontWeight="bold" mb={2}>Connection Error</Text>
                  <Text fontSize="sm">{errorMessage || "Failed to connect to XAMAN. Please try again."}</Text>
                  <Button 
                    mt={4} 
                    size="sm" 
                    colorScheme="red" 
                    onClick={handleModalClose}
                  >
                    Close
                  </Button>
                </Box>
              ) : (
                <>
                  <Text fontSize="sm" textAlign="center">
                    Scan this QR code with your XAMAN wallet app to link it to your account
                  </Text>
                  
                  {qrCodeUrl ? (
                    <Box borderRadius="md" overflow="hidden" p={4} bg="white">
                      <Image src={qrCodeUrl} alt="XAMAN QR Code" maxW="250px" />
                    </Box>
                  ) : (
                    <VStack>
                      <Spinner color={COLORS.primary} size="xl" />
                      <Text fontSize="xs" color="gray.300" mt={2}>Connecting to XAMAN API...</Text>
                    </VStack>
                  )}
                  
                  <Button
                    leftIcon={<FaWallet />}
                    colorScheme="green"
                    bg={COLORS.primary}
                    color="black"
                    size="md"
                    width="100%"
                    isDisabled={!qrCodeUrl}
                    onClick={() => deepLink && window.open(deepLink, '_blank')}
                  >
                    Open in XAMAN App
                  </Button>
                  
                  <Badge colorScheme="green" mt={2}>
                    {qrCodeUrl ? 'Waiting for wallet approval...' : 'Preparing connection...'}
                  </Badge>
                </>
              )}
              
              <Box mt={4} px={2} width="100%">
                <Divider mb={4} />
                <Text fontSize="xs" color="gray.400">
                  Linking your wallet will not give Ignita AI access to your funds. You maintain full control of your assets.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default WalletLinkingInterface;
